/**
 * LOCO — product page.
 * <variant-picker>  resolves the selected variant from real option names/values
 * <loco-gallery>    swipe counter, media switching, zoom lightbox
 * <sticky-atc>      mobile add-to-cart bar tied to the main product form
 */
(() => {
  const LOCO = (window.LOCO = window.LOCO || {});

  class VariantPicker extends HTMLElement {
    connectedCallback() {
      const json = this.querySelector('script[data-variants]');
      if (!json) return;
      try {
        this.variants = JSON.parse(json.textContent);
      } catch (error) {
        return;
      }
      this.scope = this.closest('[data-product-scope]') || document;
      this.form = document.getElementById(this.dataset.formId);
      this.idInput = this.form?.querySelector('input[name="id"][data-variant-id]');
      // Hidden id input ships disabled so the <noscript> select works without JS
      if (this.idInput) this.idInput.disabled = false;

      this.fieldsets = [...this.querySelectorAll('[data-option-position]')];
      this.addEventListener('change', (event) => {
        const input = event.target;
        if (input.matches('input[type="radio"]')) this.onChange(Number(input.closest('[data-option-position]').dataset.optionPosition));
      });
      this.refreshAvailability();
    }

    selectedOptions() {
      return this.fieldsets.map((fieldset) => fieldset.querySelector('input:checked')?.value ?? null);
    }

    onChange(changedPosition) {
      let selected = this.selectedOptions();
      let variant = this.findVariant(selected);

      // The combination doesn't exist: keep the value the shopper just picked and
      // move the other options to the closest existing (preferably available) variant.
      if (!variant) {
        const candidates = this.variants.filter((v) => v.options[changedPosition] === selected[changedPosition]);
        variant = candidates.find((v) => v.available) || candidates[0];
        if (variant) {
          variant.options.forEach((value, index) => {
            const input = [...this.fieldsets[index].querySelectorAll('input')].find((i) => i.value === value);
            if (input) input.checked = true;
          });
          selected = variant.options.slice();
        }
      }

      this.refreshAvailability();
      this.updateLegends(selected);
      this.render(variant);
    }

    findVariant(selected) {
      return this.variants.find((v) => v.options.every((value, index) => value === selected[index]));
    }

    refreshAvailability() {
      const selected = this.selectedOptions();
      this.fieldsets.forEach((fieldset, position) => {
        fieldset.querySelectorAll('input[type="radio"]').forEach((input) => {
          const matches = this.variants.filter((v) =>
            v.options.every((value, index) => (index === position ? value === input.value : value === selected[index]))
          );
          const available = matches.some((v) => v.available);
          const exists = matches.length > 0;
          input.classList.toggle('is-unavailable', !available);
          const status = input.nextElementSibling?.querySelector('[data-value-status]');
          if (status) {
            status.textContent = !exists
              ? `, ${LOCO.strings?.unavailable || 'Unavailable'}`
              : !available
              ? `, ${LOCO.strings?.soldOut || 'Sold out'}`
              : '';
          }
        });
      });
    }

    updateLegends(selected) {
      this.fieldsets.forEach((fieldset, index) => {
        const out = fieldset.querySelector('[data-selected-value]');
        if (out) out.textContent = selected[index] ?? '';
      });
    }

    render(variant) {
      const button = this.scope.querySelector('[data-add-button]');
      const label = this.scope.querySelector('[data-add-label]');
      const stickyButton = document.querySelector('[data-sticky-add]');
      const stickyLabel = document.querySelector('[data-sticky-label]');
      const strings = LOCO.strings || {};

      if (!variant) {
        this.setButton(button, label, false, strings.unavailable);
        this.setButton(stickyButton, stickyLabel, false, strings.unavailable);
        this.setText('[data-stock]', strings.unavailable, true);
        return;
      }

      if (this.idInput) this.idInput.value = variant.id;
      this.form?.dispatchEvent(new CustomEvent('loco:variant-change', { bubbles: true, detail: { variant } }));

      const text = variant.available ? strings.addToCart : strings.soldOut;
      this.setButton(button, label, variant.available, text);
      this.setButton(stickyButton, stickyLabel, variant.available, text);

      // Price
      const priceHtml = this.priceMarkup(variant);
      this.scope.querySelectorAll('[data-price]').forEach((el) => (el.innerHTML = priceHtml));
      document.querySelectorAll('[data-sticky-price]').forEach((el) => (el.innerHTML = priceHtml));

      // Availability + SKU
      const stock = this.scope.querySelector('[data-stock]');
      if (stock) {
        stock.textContent = variant.available ? stock.dataset.inStock : stock.dataset.outOfStock;
        stock.classList.toggle('product__stock--out', !variant.available);
      }
      const sku = this.scope.querySelector('[data-sku]');
      if (sku) {
        sku.hidden = !variant.sku;
        const value = sku.querySelector('[data-sku-value]');
        if (value) value.textContent = variant.sku || '';
      }

      // Media
      if (variant.featured_media_id) {
        this.scope.querySelector('loco-gallery')?.showMedia(variant.featured_media_id);
      }

      // Shareable URL without a page reload
      if (this.dataset.updateUrl !== 'false') {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    }

    setButton(button, label, enabled, text) {
      if (!button) return;
      button.disabled = !enabled;
      if (label && text) label.textContent = text;
    }

    setText(selector, text) {
      const el = this.scope.querySelector(selector);
      if (el && text) el.textContent = text;
    }

    priceMarkup(variant) {
      const money = (cents) => LOCO.formatMoney(cents);
      const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
      const saleLabel = this.dataset.saleLabel || 'Sale price';
      const regularLabel = this.dataset.regularLabel || 'Regular price';
      let html = `<span class="price price--lg${onSale ? ' price--on-sale' : ''}">`;
      if (onSale) {
        html += `<span class="visually-hidden">${saleLabel}</span><span class="price__current">${money(variant.price)}</span>`;
        html += `<span class="visually-hidden">${regularLabel}</span><s class="price__compare">${money(variant.compare_at_price)}</s>`;
      } else {
        html += `<span class="price__current">${money(variant.price)}</span>`;
      }
      if (variant.unit_price) {
        html += `<span class="price__unit">${money(variant.unit_price)} / ${variant.unit_price_reference || ''}</span>`;
      }
      html += '</span>';
      return html;
    }
  }
  if (!customElements.get('variant-picker')) customElements.define('variant-picker', VariantPicker);

  class Gallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-gallery-track]');
      this.items = [...this.querySelectorAll('[data-media-id]')];
      this.counter = this.querySelector('[data-gallery-current]');
      this.lightbox = document.getElementById(this.dataset.lightbox);

      if (this.track && this.counter && 'IntersectionObserver' in window) {
        const io = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting && entry.intersectionRatio > 0.6) {
                this.counter.textContent = String(this.items.indexOf(entry.target) + 1);
              }
            });
          },
          { root: this.track, threshold: [0.6] }
        );
        this.items.forEach((item) => io.observe(item));
      }

      this.addEventListener('click', (event) => {
        const zoom = event.target.closest('[data-zoom]');
        if (!zoom || !this.lightbox) return;
        LOCO.a11y?.openDialog(this.lightbox, zoom);
        const target = this.lightbox.querySelector(`[data-zoom-target="${zoom.dataset.zoom}"]`);
        requestAnimationFrame(() => target?.scrollIntoView({ block: 'start' }));
      });

      this.lightbox?.addEventListener('click', (event) => {
        if (event.target.matches('img')) LOCO.a11y?.closeDialog(this.lightbox);
      });
    }

    showMedia(mediaId) {
      const item = this.items.find((el) => el.dataset.mediaId === String(mediaId));
      if (!item) return;
      const desktop = window.matchMedia('(min-width: 990px)').matches;
      if (desktop) {
        // Lead with the variant's image on the stacked desktop gallery
        if (this.track.firstElementChild !== item) {
          this.track.prepend(item);
          this.items = [...this.querySelectorAll('[data-media-id]')];
        }
        const top = this.getBoundingClientRect().top + window.scrollY - (parseInt(getComputedStyle(document.documentElement).getPropertyValue('--sticky-offset'), 10) || 0) - 16;
        if (window.scrollY > top) window.scrollTo({ top, behavior: LOCO.a11y?.reduceMotion.matches ? 'auto' : 'smooth' });
      } else {
        this.track.scrollTo({ left: item.offsetLeft - this.track.offsetLeft, behavior: LOCO.a11y?.reduceMotion.matches ? 'auto' : 'smooth' });
      }
    }
  }
  if (!customElements.get('loco-gallery')) customElements.define('loco-gallery', Gallery);

  class StickyAtc extends HTMLElement {
    connectedCallback() {
      const anchor = document.querySelector(this.dataset.watch);
      if (!anchor || !('IntersectionObserver' in window)) return;
      new IntersectionObserver(([entry]) => {
        const scrolledPast = entry.boundingClientRect.top < 0;
        const visible = !entry.isIntersecting && scrolledPast;
        this.classList.toggle('is-visible', visible);
        this.toggleAttribute('inert', !visible);
      }).observe(anchor);
      this.setAttribute('inert', '');
    }
  }
  if (!customElements.get('sticky-atc')) customElements.define('sticky-atc', StickyAtc);

  // Quantity steppers on the product form (cart lines are handled by cart.js)
  document.addEventListener('click', (event) => {
    const step = event.target.closest('[data-qty-step]');
    if (!step || step.closest('loco-cart-items')) return;
    const input = step.closest('.quantity')?.querySelector('input');
    if (!input) return;
    const min = Number(input.min) || 1;
    const max = input.max ? Number(input.max) : Infinity;
    const next = Math.min(max, Math.max(min, (parseInt(input.value, 10) || min) + Number(step.dataset.qtyStep)));
    input.value = next;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });
})();
